import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { GradeInput } from '../../components/ui/GradeInput';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { getPaymentStatusLabel, getAssignmentStatusLabel, getStatusBadgeStyle, getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortApplicationsForDisplay,
  sortColumnIndicator,
} from '../../utils/adminTableSort';
import { formatDate, formatRegistrationDateTime } from '../../utils/helpers';
import {
  getAnalysisHistoryTypeLabel,
  getAnalysisHistoryActionLabel,
  getHistoryScoreColumnLabel,
} from '../../constants/displayLabels';
import { isOrdemInscricaoMode, isIntentionLetterMode } from '../../utils/selectionMode';
import {
  IntentionLetterReadOnly,
  IntentionLetterRubricForm,
  validateRubricForm,
} from '../../components/intentionLetter/IntentionLetterFields';
import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';

const STORAGE_KEY = INGRESSA_STORAGE_KEYS.filtersAnalysis;

/** Colunas da tabela Inscritos/Análise — larguras fixas só onde couber; demais colunas flexíveis */
const INSCRITOS_COL = {
  thCheck: { width: 30, minWidth: 30, maxWidth: 30, padding: '8px 4px', fontSize: '9px', textAlign: 'center', whiteSpace: 'nowrap' },
  thNum: { width: 52, minWidth: 52, maxWidth: 56, padding: '8px 6px', fontSize: '9px', whiteSpace: 'nowrap' },
  thText: { padding: '8px 10px', fontSize: '10px', whiteSpace: 'nowrap' },
  thUnit: { padding: '8px 10px', fontSize: '10px', minWidth: 120, whiteSpace: 'nowrap' },
  thQuota: { padding: '8px 4px', fontSize: '9px', minWidth: 32, maxWidth: 40, width: 36, whiteSpace: 'nowrap', textAlign: 'center' },
  thStatus: { padding: '8px 8px', fontSize: '9px', minWidth: 96, whiteSpace: 'nowrap' },
  thActions: { padding: '8px 10px', fontSize: '10px', minWidth: 130, whiteSpace: 'nowrap' },
  tdCheck: { width: 30, minWidth: 30, maxWidth: 30, padding: '8px 4px', textAlign: 'center', verticalAlign: 'middle' },
  tdNum: { width: 52, minWidth: 52, maxWidth: 56, padding: '8px 6px', fontSize: '10px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', verticalAlign: 'middle' },
  tdText: { padding: '8px 10px', fontSize: '10px', verticalAlign: 'middle', minWidth: 100, wordBreak: 'normal', overflowWrap: 'normal' },
  tdUnit: { padding: '8px 10px', fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 120, maxWidth: 200, verticalAlign: 'middle', wordBreak: 'normal', overflowWrap: 'normal' },
  tdStatus: { padding: '8px 8px', verticalAlign: 'middle', minWidth: 96, whiteSpace: 'nowrap', wordBreak: 'normal', overflowWrap: 'normal' },
  tdCompact: { padding: '8px 8px', textAlign: 'center', fontSize: '10px', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: 48 },
  tdQuota: { padding: '8px 4px', textAlign: 'center', fontSize: '10px', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: 32, maxWidth: 40, width: 36 },
  tdActions: { padding: '8px 10px', verticalAlign: 'middle', minWidth: 130, wordBreak: 'normal', overflowWrap: 'normal' },
};

const inscritosTableBadge = (baseStyle) => ({
  ...baseStyle,
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  maxWidth: 'none',
  lineHeight: 1.25,
  display: 'inline-block',
});

const AnalysisManager = ({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [applications, setApplications] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [assignmentType, setAssignmentType] = useState(''); // 'application' | 'reassignment'
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const [filterPayment, setFilterPayment] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p.filterPayment || ''; }
    } catch (e) {}
    return '';
  });
  const [filterAnalysis, setFilterAnalysis] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p.filterAnalysis || ''; }
    } catch (e) {}
    return '';
  });
  const [filterCandidate, setFilterCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCandidate ? String(p.filterCandidate) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterAnalyst, setFilterAnalyst] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) {
        const p = JSON.parse(s);
        const v = p && p.filterAnalyst ? String(p.filterAnalyst) : '';
        if (v === 'not_assigned') return '';
        return v;
      }
    } catch (e) {}
    return '';
  });
  const [filterCourse, setFilterCourse] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCourse ? String(p.filterCourse) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterCampus, setFilterCampus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCampus ? String(p.filterCampus) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterQuota, setFilterQuota] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterQuota ? String(p.filterQuota) : ''; }
    } catch (e) {}
    return '';
  });
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedApplicationToView, setSelectedApplicationToView] = useState(null);
  const [viewModalTab, setViewModalTab] = useState('details');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [selectedApplicationForAnalysis, setSelectedApplicationForAnalysis] = useState(null);
  const [selectedForBulkCurricular, setSelectedForBulkCurricular] = useState(new Set());
  const [bulkCurricularAnalyst, setBulkCurricularAnalyst] = useState('');
  const [equitableCurricularAnalysts, setEquitableCurricularAnalysts] = useState(new Set());

  // Estados para análise de notas
  const [analysisForm, setAnalysisForm] = useState({
    grades: {},
    status: 'pending',
    rejection_reason: '',
    notes: ''
  });

  // Função para validar se motivo de indeferimento foi selecionado
  const validateRejectionReason = () => {
    if (analysisForm.status === 'rejected' && !analysisForm.rejection_reason) {
      addNotification('Campo obrigatório', 'Selecione um motivo de indeferimento', 'warning');
      return false;
    }
    return true;
  };

  const { addNotification } = useNotifications();

  // Função para determinar status de pagamento (mesma lógica da página Pagamentos)
  const getPaymentStatusForAnalysis = useCallback((application) => {
    // Pagamento confirmado pelo admin tem prioridade
    if (application.payment_status === 'paid') return 'paid';
    // Verificar status de isenção
    if (application.exemption_status) {
      switch (application.exemption_status) {
        case 'approved':
          return 'exempted';
        case 'rejected':
          return 'exemption_rejected';
        case 'returned':
          return 'exemption_returned';
        case 'appealed':
          return 'exemption_appealed';
        case 'appeal_approved':
          return 'exemption_appeal_approved';
        case 'appeal_rejected':
          return 'exemption_appeal_rejected';
        case 'pending': {
          const hasExemptionAnalyst = application.exemption_analyst_id || application.exemption_analyst_name;
          if (!hasExemptionAnalyst) return 'pending_exemption_not_assigned';
          return application.exemption_request_status === 'analyzing'
            ? 'pending_exemption'
            : 'pending_exemption_assigned';
        }
        default:
          break;
      }
    }
    
    // Se não há status de isenção, verificar payment_status
    if (application.payment_status) {
      return application.payment_status;
    }
    
    return 'pending';
  }, []);

  // Função para determinar status de análise curricular (SEPARADO de isenção)
  const getAnalysisStatusForDisplay = useCallback((application) => {
    // Inscrição excluída pelo candidato (troca com matriz diferente)
    if (application.excluded_at) {
      return 'excluded';
    }
    // Se isenção foi rejeitada e ainda não pagou, não analisar. Se já pagou (payment_status=paid), pode analisar.
    if (application.exemption_status === 'rejected' && application.payment_status !== 'paid') {
      return 'not_analyzed';
    }
    
    // Se análise curricular foi devolvida
    if (application.analysis_status === 'returned') {
      return 'returned';
    }
    
    // Se tem analista de análise curricular atribuído mas ainda não analisou
    if ((application.curricular_analyst_id || application.analyst_id) && !application.analysis_status) {
      return 'pending';
    }
    
    // Usar o status de análise curricular existente
    if (application.analysis_status) {
      return application.analysis_status;
    }
    
    // Status padrão: não analisada (primeira situação)
    return 'not_analyzed';
  }, []);

  const loadData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const [applicationsData, paymentsData, analystsData, usersData, processesData, coursesData, reasonsData, analysesData] = await Promise.all([
        apiClient.getApplications(),
        apiClient.getPayments(),
        apiClient.getUsers('analyst'),
        apiClient.getUsers(),
        apiClient.getProcesses(true),
        apiClient.getCourses(),
        apiClient.getRejectionReasons({ context: 'list' }),
        apiClient.getAnalyses()
      ]);
      const apps = Array.isArray(applicationsData) ? applicationsData : [];
      const paymentsList = Array.isArray(paymentsData) ? paymentsData : [];
      const analysts = Array.isArray(analystsData) ? analystsData : [];
      // Sincronizar payment_status com a API de pagamentos (igual ao PaymentManager)
      const syncedApps = apps.map(app => {
        const payment = paymentsList.find(p =>
          p.application_id === app.id || (p.user_id === (app.candidate_id || app.user_id) && p.process_id === app.process_id)
        );
        const appWithPayment = payment ? { ...app, payment_status: payment.status } : app;
        const analyst = analysts.find(a => a.id === appWithPayment.analyst_id);
        // Analistas estão na lista; admin não – preservar analyst_name do backend (ex: 'Administrador')
        const analystName = analyst ? (analyst.name || null) : (appWithPayment.analyst_name || null);
        return { ...appWithPayment, analyst_name: analystName };
      });
      const processedApplications = syncedApps;

      setApplications(processedApplications);
      setAnalysts(analysts);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
      setAnalyses(Array.isArray(analysesData) ? analysesData : []);
      
      console.log('🔍 AnalysisManager - Dados carregados:');
      console.log('  - Aplicações:', processedApplications?.length || 0);
      console.log('  - Aplicações com analyst_name:', (processedApplications || []).filter(app => app.analyst_name).length);
      console.log('  - Aplicações com status exemption_pending_assignment:', (processedApplications || []).filter(app => app.status === 'exemption_pending_assignment').length);
    } catch (err) {
      if (!isBackground) setError('Erro ao carregar dados');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedForBulkCurricular(new Set());
    setBulkCurricularAnalyst('');
    setEquitableCurricularAnalysts(new Set());
    setFilterAnalyst('');
    const { id } = parseAdminSelectedProcessId(selectedProcessId);
    const proc = id != null ? processes.find((p) => p.id === id) : null;
    if (isOrdemInscricaoMode(proc?.selection_mode)) {
      setTableSort({ key: 'registered_at', dir: 'asc' });
    } else {
      setTableSort(initialAdminTableSort());
    }
  }, [selectedProcessId, processes]);

  const hasModalOpen = showAnalysisModal || showAssignmentModal || showViewModal;

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        filterPayment, filterAnalysis,
        filterCandidate: String(filterCandidate || ''),
        filterAnalyst: String(filterAnalyst || ''),
        filterCourse: String(filterCourse || ''),
        filterCampus: String(filterCampus || ''),
        filterQuota: String(filterQuota || '')
      }));
    } catch (e) {}
  }, [filterPayment, filterAnalysis, filterCandidate, filterAnalyst, filterCourse, filterCampus, filterQuota]);

  useEffect(() => {
    if (hasModalOpen) return;
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData, hasModalOpen]);

  const clearFilters = useCallback(() => {
    setFilterPayment('');
    setFilterAnalysis('');
    setFilterCandidate('');
    setFilterAnalyst('');
    setFilterCourse('');
    setFilterCampus('');
    setFilterQuota('');
  }, []);

  const handleViewApplication = useCallback((application) => {
    setSelectedApplicationToView(application);
    setViewModalTab('details');
    setShowViewModal(true);
  }, []);

  const loadAnalysisHistory = useCallback(async (appId) => {
    if (!appId) return;
    setHistoryLoading(true);
    try {
      const data = await apiClient.getAnalysisHistory(appId);
      setAnalysisHistory(Array.isArray(data) ? data : []);
    } catch {
      setAnalysisHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showViewModal && selectedApplicationToView && viewModalTab === 'history') {
      loadAnalysisHistory(selectedApplicationToView.id);
    }
  }, [showViewModal, selectedApplicationToView?.id, viewModalTab, loadAnalysisHistory]);

  const handleAssign = useCallback((item, type) => {
    setSelectedItem(item);
    setAssignmentType(type);
    setShowAssignmentModal(true);
  }, []);

  const handleAssignmentSubmit = useCallback(async () => {
    
    if (!selectedAnalyst) {
      setError('Selecione um analista');
      return;
    }

    try {
      setLoading(true);
      
      if (assignmentType === 'application') {
        // Obter o nome do analista selecionado
        const selectedAnalystData = analysts.find(a => a.id === parseInt(selectedAnalyst));
        const analystName = selectedAnalystData ? selectedAnalystData.name : null;

        const { error: updateError } = await apiClient.updateApplication(selectedItem.id, {
          analyst_id: parseInt(selectedAnalyst),
          status: 'assigned',
          analysis_status: 'pending',
          assignment_type: 'curricular'
        });
        if (updateError) {
          setError('Erro ao atribuir inscrição');
        } else {
          addNotification('Sucesso', 'Inscrição atribuída com sucesso', 'success');
          setShowAssignmentModal(false);
          setSelectedItem(null);
          setSelectedAnalyst('');
          loadData();
        }
      } else if (assignmentType === 'reassignment') {
        // Obter o nome do analista selecionado
        const selectedAnalystData = analysts.find(a => a.id === parseInt(selectedAnalyst));
        const analystName = selectedAnalystData ? selectedAnalystData.name : null;

        // Verificar se está tentando reatribuir para o mesmo analista
        if (selectedItem.analyst_id === parseInt(selectedAnalyst)) {
          setError('A aplicação já está atribuída a este analista');
          return;
        }

        const { error: updateError } = await apiClient.updateApplication(selectedItem.id, {
          analyst_id: parseInt(selectedAnalyst),
          status: 'assigned',
          analysis_status: 'pending',
          assignment_type: 'curricular'
        });
        if (updateError) {
          setError('Erro ao reatribuir inscrição');
        } else {
          addNotification('Sucesso', `Inscrição reatribuída para ${analystName}`, 'success');
          setShowAssignmentModal(false);
          setSelectedItem(null);
          setSelectedAnalyst('');
          loadData();
        }
      }
    } catch (err) {
      setError('Erro ao atribuir item');
    } finally {
      setLoading(false);
    }
  }, [selectedAnalyst, assignmentType, selectedItem, addNotification, loadData, analysts]);

  const handleDeleteApplication = useCallback(async (app) => {
    if (!window.confirm(`Excluir inscrição #${app.id} (${app.candidate_name})? Isso remove análise, isenção e pagamento.`)) return;
    try {
      setLoading(true);
      const { error } = await apiClient.deleteApplication(app.id);
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Inscrição excluída', 'success');
      await loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao excluir', 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification, loadData]);

  // Função getPaymentStatusLabel agora importada do arquivo padronizado

  // Função getPaymentStatusStyle agora importada do arquivo padronizado

  // Função getAssignmentStatusLabel agora importada do arquivo padronizado

  // Função getStatusBadgeStyle agora importada do arquivo padronizado

  const getAnalysisStatusLabel = useCallback((status) => {
    switch (status) {
      case 'pending':
        return 'Em Análise';
      case 'approved':
        return 'Deferida';
      case 'returned':
        return 'Não reatribuída';
      case 'rejected':
        return 'Indeferida';
      case 'not_analyzed':
        return 'Não atribuída';
      case 'excluded':
        return 'Excluída';
      default:
        return 'Não atribuída';
    }
  }, []);

  const getAnalysisStatusStyle = useCallback((status) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500'
    };
    
    switch (status) {
      case 'approved':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'returned':
        return { ...baseStyle, backgroundColor: '#CFFAFE', color: '#0E7490' };
      case 'rejected':
        return { ...baseStyle, backgroundColor: '#FEE2E2', color: '#991B1B' };
      case 'pending':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'not_analyzed':
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#6B7280' };
      case 'excluded':
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#6B7280' };
      default:
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#6B7280' };
    }
  }, []);

  const selectedProcess = useMemo(() => {
    const { id } = parseAdminSelectedProcessId(selectedProcessId);
    if (id == null) return null;
    return processes.find((p) => p.id === id) || null;
  }, [selectedProcessId, processes]);

  const historyScoreColumnLabel = getHistoryScoreColumnLabel(selectedProcess?.selection_mode);
  const isOrdemProcess = isOrdemInscricaoMode(selectedProcess?.selection_mode);
  const isLetterProcess = isIntentionLetterMode(selectedProcess?.selection_mode);
  const processHasFee = !!selectedProcess?.has_fee;
  const processUsesQuotas = selectedProcess?.use_quotas === true;

  const isPaymentReadyForCurricular = useCallback((application) => {
    const process = processes.find((p) => p.id === application.process_id);
    if (process && !process.has_fee) return true;
    const ps = getPaymentStatusForAnalysis(application);
    return ps === 'paid' || ps === 'exempted' || ps === 'exemption_appeal_approved';
  }, [processes, getPaymentStatusForAnalysis]);

  const letterAnalysisRequiredByCourse = useMemo(() => {
    if (!isLetterProcess) return null;
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return {};
    const eligible = {};
    applications.forEach((app) => {
      if (app.excluded_at || Number(app.process_id) !== pid) return;
      if (!isPaymentReadyForCurricular(app)) return;
      eligible[app.course_id] = (eligible[app.course_id] || 0) + 1;
    });
    const map = {};
    courses.forEach((c) => {
      if (Number(c.process_id) !== pid) return;
      map[c.id] = (eligible[c.id] || 0) > (c.vacancies || c.total_vacancies || 0);
    });
    return map;
  }, [isLetterProcess, applications, courses, selectedProcessId, isPaymentReadyForCurricular]);

  const applicationRequiresLetterAnalysis = useCallback((application) => {
    if (!letterAnalysisRequiredByCourse) return true;
    return !!letterAnalysisRequiredByCourse[application.course_id];
  }, [letterAnalysisRequiredByCourse]);

  useEffect(() => {
    if (!processHasFee && filterPayment) setFilterPayment('');
  }, [processHasFee, filterPayment]);

  useEffect(() => {
    if (!processUsesQuotas && filterQuota) setFilterQuota('');
  }, [processUsesQuotas, filterQuota]);

  const analystsForProcess = useMemo(() => {
    const { empty, invalid, id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty) return analysts;
    if (invalid || pid == null) return [];
    return analysts.filter(a => {
      const pids = a.process_ids || [];
      return pids.length === 0 || pids.includes(pid);
    });
  }, [analysts, selectedProcessId]);

  const curricularAnalystFilterOptions = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return [];
    const byId = new Map();
    analystsForProcess.forEach((a) => byId.set(String(a.id), { id: a.id, name: a.name || `Usuário #${a.id}` }));
    applications.forEach((app) => {
      if (app.excluded_at || Number(app.process_id) !== pid) return;
      const id = app.analyst_id || app.curricular_analyst_id;
      if (!id) return;
      const key = String(id);
      if (byId.has(key)) return;
      let name = app.analyst_name || app.curricular_analyst_name;
      if (!name) {
        const u = users.find((u) => Number(u.id) === Number(id));
        if (u) name = u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${id}`);
        else name = `Usuário #${id}`;
      }
      byId.set(key, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [selectedProcessId, analystsForProcess, applications, users]);

  useEffect(() => {
    if (!filterAnalyst || !selectedProcessId) return;
    const ok = curricularAnalystFilterOptions.some((o) => String(o.id) === String(filterAnalyst));
    if (!ok) setFilterAnalyst('');
  }, [filterAnalyst, selectedProcessId, curricularAnalystFilterOptions]);

  const curricularAnalystCellLabel = useCallback((app) => {
    const id = app.analyst_id || app.curricular_analyst_id;
    const label = app.analyst_name || app.curricular_analyst_name;
    if (label) return label;
    if (!id) return 'Não atribuído';
    const u = users.find((u) => Number(u.id) === Number(id));
    if (u) return u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${id}`);
    return `Usuário #${id}`;
  }, [users]);

  const filteredApplications = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    return applications.filter((app) => {
      if (!procSel.empty) {
        if (procSel.invalid) return false;
        if (app.process_id !== procSel.id) return false;
      }
      if (filterPayment) {
        const paymentStatus = getPaymentStatusForAnalysis(app);
        if (paymentStatus !== filterPayment) return false;
      }
      if (filterAnalysis) {
        const displayStatus = getAnalysisStatusForDisplay(app);
        if (displayStatus !== filterAnalysis) return false;
      }
      const q = (filterCandidate || '').trim().toLowerCase();
      if (q && ![app.candidate_name, app.candidate_cpf].some((v) => v && String(v).toLowerCase().includes(q))) return false;
      const curricularAnalystId = app.analyst_id || app.curricular_analyst_id;
      const matchesAnalyst =
        !filterAnalyst ||
        (curricularAnalystId != null && String(curricularAnalystId) === String(filterAnalyst));
      const matchesCourse = !filterCourse || String(app.course_name || '') === String(filterCourse);
      const matchesCampus = !filterCampus || String(app.course_campus || '') === String(filterCampus);
      const matchesQuota = !filterQuota || String(app.quota_type || 'AC') === String(filterQuota);
      return matchesAnalyst && matchesCourse && matchesCampus && matchesQuota;
    });
  }, [
    applications,
    selectedProcessId,
    filterPayment,
    filterAnalysis,
    filterCandidate,
    filterAnalyst,
    filterCourse,
    filterCampus,
    filterQuota,
    getPaymentStatusForAnalysis,
    getAnalysisStatusForDisplay
  ]);

  const sortedApplications = useMemo(
    () => sortApplicationsForDisplay(filteredApplications, tableSort, {
      registrationOrderAsc: isOrdemProcess && !tableSort.key,
    }),
    [filteredApplications, tableSort, isOrdemProcess]
  );

  const pendingCurricularForBulk = useMemo(() => {
    return filteredApplications.filter(app => {
      return isPaymentReadyForCurricular(app) &&
        !app.analyst_id && !app.curricular_analyst_id &&
        !app.excluded_at;
    });
  }, [filteredApplications, isPaymentReadyForCurricular]);

  const courseFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const names = new Set();
    applications.forEach((app) => {
      if (app.excluded_at) return;
      if (!procSel.empty && !procSel.invalid && app.process_id !== procSel.id) return;
      if (!procSel.empty && procSel.invalid) return;
      if (app.course_name) names.add(String(app.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, selectedProcessId]);

  const campusFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const campuses = new Set();
    applications.forEach((app) => {
      if (app.excluded_at) return;
      if (!procSel.empty && !procSel.invalid && app.process_id !== procSel.id) return;
      if (!procSel.empty && procSel.invalid) return;
      if (app.course_campus) campuses.add(String(app.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, selectedProcessId]);

  const quotaFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const quotas = new Set();
    applications.forEach((app) => {
      if (app.excluded_at) return;
      if (!procSel.empty && !procSel.invalid && app.process_id !== procSel.id) return;
      if (!procSel.empty && procSel.invalid) return;
      quotas.add(String(app.quota_type || 'AC'));
    });
    return Array.from(quotas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, selectedProcessId]);

  const handleBulkAssignCurricular = useCallback(async () => {
    if (!bulkCurricularAnalyst || selectedForBulkCurricular.size === 0) {
      addNotification('Selecione um analista e ao menos uma inscrição', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkCurricular);
    try {
      const { data, error } = await apiClient.bulkAssignCurricular(ids, parseInt(bulkCurricularAnalyst, 10));
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const analyst = analystsForProcess.find(a => a.id === parseInt(bulkCurricularAnalyst, 10));
      addNotification('Atribuição em massa', `${count} inscrição(ões) atribuída(s) a ${analyst?.name || 'analista'}`, 'success');
      setSelectedForBulkCurricular(new Set());
      setBulkCurricularAnalyst('');
      await loadData();
    } catch (err) {
      addNotification('Erro ao atribuir', err.message || 'Erro', 'error');
    }
  }, [bulkCurricularAnalyst, selectedForBulkCurricular, analystsForProcess, addNotification, loadData]);

  const handleBulkAssignCurricularEquitable = useCallback(async () => {
    if (selectedForBulkCurricular.size === 0 || equitableCurricularAnalysts.size < 2) {
      addNotification('Atenção', 'Selecione inscrições e marque ao menos 2 analistas para distribuição equânime.', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkCurricular);
    const analystIds = Array.from(equitableCurricularAnalysts)
      .map((x) => parseInt(x, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    try {
      const { data, error } = await apiClient.bulkAssignCurricularEquitable(ids, analystIds);
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const per = data?.per_analyst || {};
      const parts = Object.entries(per)
        .filter(([, c]) => c > 0)
        .map(([id, c]) => {
          const an = analystsForProcess.find((x) => String(x.id) === String(id));
          return `${an?.name || id}: ${c}`;
        });
      addNotification(
        'Distribuição equânime',
        `${count} inscrição(ões) atribuída(s)${parts.length ? ` — ${parts.join('; ')}` : ''}`,
        'success'
      );
      setSelectedForBulkCurricular(new Set());
      setEquitableCurricularAnalysts(new Set());
      await loadData();
    } catch (err) {
      addNotification('Erro ao distribuir', err.message || 'Erro', 'error');
    }
  }, [selectedForBulkCurricular, equitableCurricularAnalysts, analystsForProcess, addNotification, loadData]);

  const canAssignApplication = useCallback((application) => {
    return application.payment_status === 'paid' || application.payment_status === 'exempted';
  }, []);

  const handleAnalyze = useCallback((application) => {
    setSelectedApplicationForAnalysis(application);
    
    const course = courses.find(c => c.id === application.course_id);
    const processForApp = processes.find((p) => p.id === application.process_id);
    const letterMode = isIntentionLetterMode(processForApp?.selection_mode);
    const isEdit = application.analysis_status === 'approved' || application.analysis_status === 'rejected';
    let initialGrades = {};
    if (letterMode && course?.letter_rubric?.length) {
      course.letter_rubric.forEach((row) => {
        initialGrades[row.id] = isEdit ? 0 : '';
      });
      const savedGrades = application.analysis_grades;
      if (savedGrades && typeof savedGrades === 'object') {
        course.letter_rubric.forEach((row) => {
          if (savedGrades[row.id] !== undefined && savedGrades[row.id] !== null) {
            initialGrades[row.id] = savedGrades[row.id];
          }
        });
      }
    } else if (course && course.subjects) {
      course.subjects.forEach(subject => {
        initialGrades[subject.serie] = {};
        subject.disciplinas.forEach(disciplina => {
          // Nova análise: não preencher (obrigar digitar); Edição: preencher 0 para overlay
          initialGrades[subject.serie][disciplina.name] = isEdit ? 0 : undefined;
        });
      });
    }
    
    const savedGrades = application.analysis_grades;
    if (!letterMode && savedGrades && typeof savedGrades === 'object') {
      Object.keys(savedGrades).forEach(serie => {
        if (initialGrades[serie]) {
          Object.keys(savedGrades[serie] || {}).forEach(disc => {
            if (initialGrades[serie][disc] !== undefined) {
              initialGrades[serie][disc] = savedGrades[serie][disc];
            }
          });
        }
      });
    }
    
    setAnalysisForm({
      grades: initialGrades,
      status: isEdit ? application.analysis_status : 'pending',
      rejection_reason: isEdit ? (application.curricular_rejection_reason || application.rejection_reason || '') : '',
      notes: isEdit ? (application.curricular_rejection_notes || application.rejection_notes || '') : '',
    });
    
    setShowAnalysisModal(true);
  }, [courses, processes]);

  const handleRubricScoreChange = useCallback((criterionId, value) => {
    setAnalysisForm((prev) => ({
      ...prev,
      grades: { ...prev.grades, [criterionId]: value },
    }));
  }, []);

  const handleDownloadDocuments = useCallback((application, docIndex = null) => {
    const docs = application.documents || application.curricular_documents || [];
    if (docs.length === 0) {
      addNotification('Aviso', 'Nenhum documento disponível para download', 'warning');
      return;
    }
    const indices = docIndex !== null ? [docIndex] : docs.map((_, i) => i);
    if (docIndex === null) addNotification('Download', `Baixando ${docs.length} documento(s)...`, 'info');
    indices.forEach(async (i) => {
      try {
        const blob = await apiClient.downloadDocument({ applicationId: application.id, type: 'curricular', index: i });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = docs[i]?.name || `documento_${i + 1}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        addNotification('Erro', err.message || `Falha ao baixar documento ${i + 1}`, 'error');
      }
    });
  }, [addNotification]);

  // Função para manipular mudanças nas notas
  const handleGradeChange = useCallback((serie, disciplina, value) => {
    setAnalysisForm(prev => ({
      ...prev,
      grades: {
        ...prev.grades,
        [serie]: {
          ...prev.grades[serie],
          [disciplina]: value
        }
      }
    }));
  }, []);

  // Função para calcular média ponderada
  const calculateWeightedAverage = useCallback((grades, courseSubjects) => {
    if (!grades || !courseSubjects) return 0;
    
    let totalSeriesAverage = 0;
    let seriesCount = 0;
    
    courseSubjects.forEach(subject => {
      const serieGrades = grades[subject.serie];
      if (serieGrades) {
        let serieWeightedSum = 0;
        let serieTotalWeight = 0;
        
        subject.disciplinas.forEach(disciplina => {
          const grade = serieGrades[disciplina.name];
          if (grade !== undefined && grade !== null && grade !== '' && !isNaN(parseFloat(grade))) {
            serieWeightedSum += parseFloat(grade) * disciplina.weight;
            serieTotalWeight += disciplina.weight;
          }
        });
        
        if (serieTotalWeight > 0) {
          const serieAverage = serieWeightedSum / serieTotalWeight;
          totalSeriesAverage += serieAverage;
          seriesCount++;
        }
      }
    });
    
    return seriesCount > 0 ? Math.round((totalSeriesAverage / seriesCount) * 100) / 100 : 0;
  }, []);

  // Função para calcular média atual
  const calculateCurrentAverage = useCallback(() => {
    const course = courses.find(c => c.id === selectedApplicationForAnalysis?.course_id);
    if (!course || !course.subjects) return 0;
    
    return calculateWeightedAverage(analysisForm.grades, course.subjects);
  }, [selectedApplicationForAnalysis, analysisForm.grades, courses, calculateWeightedAverage]);

  // Validar se todas as notas foram preenchidas (obrigatório ao deferir)
  const validateAllGrades = useCallback((grades, courseSubjects) => {
    if (!courseSubjects || !grades) return { isValid: false, missingGrades: [] };
    const missing = [];
    courseSubjects.forEach(s => {
      (s.disciplinas || []).forEach(d => {
        const discName = d.name || d.nome;
        const v = grades[s.serie]?.[discName];
        if (v === undefined || v === null || v === '' || (typeof v === 'number' && isNaN(v))) {
          missing.push(`${s.serie} - ${discName || 'Disciplina'}`);
        }
      });
    });
    return { isValid: missing.length === 0, missingGrades: missing };
  }, []);

  // Função para salvar análise
  const saveAnalysis = useCallback(async () => {
    if (!selectedApplicationForAnalysis) return;

    const processForApp = processes.find((p) => p.id === selectedApplicationForAnalysis.process_id);
    const ordemMode = isOrdemInscricaoMode(processForApp?.selection_mode);

    if (ordemMode) {
      if (analysisForm.status !== 'approved' && analysisForm.status !== 'rejected') {
        addNotification('Validação', 'Selecione Deferida ou Indeferida.', 'warning');
        return;
      }
      if (analysisForm.status === 'rejected' && !analysisForm.rejection_reason) {
        addNotification('❌ Validação', 'Selecione o motivo de indeferimento.', 'warning');
        return;
      }
      try {
        const { error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
          analysis_status: analysisForm.status,
          status: analysisForm.status,
          ...(analysisForm.status === 'rejected' ? {
            ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
            rejection_notes: (analysisForm.notes || '').trim(),
          } : {}),
        });
        if (updateError) throw new Error(updateError);
        addNotification('✅ Análise Salva', analysisForm.status === 'rejected' ? 'Indeferimento registrado.' : 'Documentação deferida.', 'success');
        setShowAnalysisModal(false);
        setSelectedApplicationForAnalysis(null);
        setAnalysisForm({ grades: {}, status: 'pending', rejection_reason: '', notes: '' });
        loadData();
      } catch (error) {
        addNotification('❌ Erro', 'Erro ao salvar análise', 'error');
      }
      return;
    }

    const letterMode = isIntentionLetterMode(processForApp?.selection_mode);
    const course = courses.find(c => c.id === selectedApplicationForAnalysis.course_id);
    if (letterMode) {
      if (analysisForm.status === 'approved') {
        const rubricCheck = validateRubricForm(course, analysisForm.grades);
        if (!rubricCheck.ok) {
          addNotification('❌ Validação', rubricCheck.message, 'warning');
          return;
        }
        await apiClient.createAnalysis({
          application_id: selectedApplicationForAnalysis.id,
          status: analysisForm.status,
          final_score: rubricCheck.total,
          grades: analysisForm.grades,
        });
      }
      try {
        const { error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
          analysis_status: analysisForm.status,
          status: analysisForm.status,
          ...(analysisForm.status === 'rejected' ? {
            ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
            rejection_notes: (analysisForm.notes || '').trim(),
          } : {}),
        });
        if (updateError) throw new Error(updateError);
        addNotification('✅ Análise Salva', 'Carta de intenção analisada.', 'success');
        setShowAnalysisModal(false);
        setSelectedApplicationForAnalysis(null);
        setAnalysisForm({ grades: {}, status: 'pending', rejection_reason: '', notes: '' });
        loadData();
      } catch (error) {
        addNotification('❌ Erro', 'Erro ao salvar análise', 'error');
      }
      return;
    }

    const courseCurricular = courses.find(c => c.id === selectedApplicationForAnalysis.course_id);
    if (!courseCurricular || !courseCurricular.subjects) {
      addNotification('❌ Erro', 'Curso não encontrado ou disciplinas não configuradas', 'error');
      return;
    }

    // Ao deferir: exige todas as notas preenchidas (incluindo 0)
    if (analysisForm.status === 'approved') {
      const validation = validateAllGrades(analysisForm.grades, courseCurricular.subjects);
      if (!validation.isValid) {
        addNotification('❌ Campos obrigatórios', `Preencha todas as notas: ${validation.missingGrades.slice(0, 5).join(', ')}${validation.missingGrades.length > 5 ? '...' : ''}`, 'warning');
        return;
      }
    }

    const finalAverage = calculateWeightedAverage(analysisForm.grades, courseCurricular.subjects);
    
    try {
      const { error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
        analysis_status: analysisForm.status,
        status: analysisForm.status,
        ...(analysisForm.status === 'rejected' ? {
          ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
          rejection_notes: (analysisForm.notes || '').trim(),
        } : {}),
      });
      if (updateError) throw new Error(updateError);

      await apiClient.createAnalysis({
        application_id: selectedApplicationForAnalysis.id,
        status: analysisForm.status,
        final_score: finalAverage,
        grades: analysisForm.grades
      });

      addNotification('✅ Análise Salva', `Média final: ${finalAverage.toFixed(2)}`, 'success');
      setShowAnalysisModal(false);
      setSelectedApplicationForAnalysis(null);
      setAnalysisForm({
        grades: {},
        status: 'pending',
        rejection_reason: '',
        notes: ''
      });
      
      // Recarregar dados para atualizar a tabela
      loadData();
    } catch (error) {
      addNotification('❌ Erro', 'Erro ao salvar análise', 'error');
    }
  }, [selectedApplicationForAnalysis, analysisForm, courses, processes, calculateWeightedAverage, validateAllGrades, addNotification, loadData]);

  if (loading && applications.length === 0 && !hasModalOpen) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={{...styles.flexBetween, ...styles.mb6}}>
        <div>
          <h2 style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
            {isOrdemProcess ? '📋 Gestão de Inscritos' : '📚 Gestão de Análises'}
          </h2>
          <p style={{ fontSize: '16px', color: '#6B7280', margin: 0 }}>
            {isOrdemProcess ? 'Atribua inscrições para verificação de documentos' : 'Atribua inscrições para análise'}
          </p>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <label style={styles.label}>Processo seletivo:</label>
        <select
          value={selectedProcessId}
          onChange={setSelectedProcessId}
          style={{ ...styles.select, maxWidth: '400px' }}
        >
          <option value="">Selecione o processo</option>
          {processes.map((p) => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Legenda de ícones */}
      <div style={{ ...styles.card, marginBottom: '16px', padding: '14px 20px', backgroundColor: '#F9FAFB' }}>
        <div style={{ fontSize: '15px', color: '#374151', display: 'flex', flexWrap: 'wrap', gap: '14px 24px', alignItems: 'center' }}>
          <span><strong>Legenda:</strong></span>
          <span>{ACTION_ICONS.assign} Atribuir</span>
          <span>{ACTION_ICONS.reassign} Reatribuir</span>
          <span>{ACTION_ICONS.fix} Corrigir</span>
          <span>{ACTION_ICONS.view} Ver detalhes</span>
          <span>{ACTION_ICONS.analyze} Analisar</span>
          <span>{ACTION_ICONS.edit} Editar</span>
          <span>{ACTION_ICONS.delete} Excluir</span>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={styles.cardTitle}>Filtros</h3>
          <button
            onClick={clearFilters}
            style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px' }}
            title="Remover todos os filtros"
          >
            🗑️ Limpar filtros
          </button>
        </div>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Candidato</label>
            <input
              type="text"
              value={filterCandidate}
              onChange={(e) => setFilterCandidate(e.target.value)}
              placeholder="Nome, CPF..."
              style={{ ...styles.select, fontFamily: 'inherit' }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Analista curricular</label>
            <select
              value={filterAnalyst}
              onChange={(e) => setFilterAnalyst(e.target.value)}
              style={{
                ...styles.select,
                opacity: selectedProcessId ? 1 : 0.65,
              }}
              disabled={!selectedProcessId}
              title={
                selectedProcessId
                  ? 'Analistas do processo ou que já têm análise curricular atribuída neste processo (inclui admin)'
                  : 'Selecione o processo seletivo para habilitar este filtro'
              }
            >
              <option value="">{selectedProcessId ? 'Todos' : 'Selecione o processo…'}</option>
              {curricularAnalystFilterOptions.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
          </div>
          {processHasFee && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Pagamento</label>
            <select
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as situações</option>
              <option value="pending">Pendente</option>
              <option value="pending_exemption_not_assigned">Isenção Não Atribuída</option>
              <option value="pending_exemption_assigned">Isenção Atribuída</option>
              <option value="pending_exemption">Isenção em Análise</option>
              <option value="exemption_rejected">Isenção Rejeitada</option>
              <option value="exemption_returned">Isenção Devolvida</option>
              <option value="exemption_appealed">Recurso de Isenção em Análise</option>
              <option value="exemption_appeal_approved">Recurso de Isenção Aprovado</option>
              <option value="exemption_appeal_rejected">Recurso de Isenção Rejeitado</option>
              <option value="paid">Pago</option>
              <option value="exempted">Isento</option>
            </select>
          </div>
          )}
          <div style={styles.formGroup}>
            <label style={styles.label}>Análise</label>
            <select
              value={filterAnalysis}
              onChange={(e) => setFilterAnalysis(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as análises</option>
              <option value="not_analyzed">Não atribuída</option>
              <option value="pending">Em Análise</option>
              <option value="approved">Deferida</option>
              <option value="returned">Não reatribuída</option>
              <option value="rejected">Indeferida</option>
              <option value="excluded">Excluída</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((campus) => (
                <option key={campus} value={campus}>{campus}</option>
              ))}
            </select>
          </div>
          {processUsesQuotas && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Cota</label>
            <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
              <option value="">Todas as cotas</option>
              {quotaFilterOptions.map((quota) => (
                <option key={quota} value={quota}>{quota}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </div>

      {/* Inscrições */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3 style={styles.cardTitle}>Inscrições</h3>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>
              Total: {filteredApplications.length} | Pagas/Isentas: {filteredApplications.filter(app => ['paid', 'exempted'].includes(getPaymentStatusForAnalysis(app))).length}
            </span>
          </div>
          {pendingCurricularForBulk.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setSelectedForBulkCurricular(new Set(pendingCurricularForBulk.map(a => a.id)))} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}>
                Selecionar todos ({pendingCurricularForBulk.length})
              </button>
              <button type="button" onClick={() => setSelectedForBulkCurricular(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}>
                Desmarcar
              </button>
              <select value={bulkCurricularAnalyst} onChange={(e) => setBulkCurricularAnalyst(e.target.value)} style={{ ...styles.select, padding: '6px 10px', minWidth: 180 }}>
                <option value="">Atribuir a...</option>
                {analystsForProcess.map(a => (
                  <option key={a.id} value={String(a.id)}>{a.name}</option>
                ))}
              </select>
              <button type="button" onClick={handleBulkAssignCurricular} disabled={!bulkCurricularAnalyst || selectedForBulkCurricular.size === 0} style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12 }}>
                Atribuir selecionados ({selectedForBulkCurricular.size})
              </button>
              <div style={{ flex: '1 1 100%', width: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>Distribuição equânime (rodízio)</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                  Marque 2 ou mais analistas. As inscrições marcadas serão alternadas entre eles.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
                  {analystsForProcess.map((a) => (
                    <label key={a.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={equitableCurricularAnalysts.has(String(a.id))}
                        onChange={(e) => {
                          setEquitableCurricularAnalysts((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(String(a.id));
                            else next.delete(String(a.id));
                            return next;
                          });
                        }}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button type="button" onClick={() => setEquitableCurricularAnalysts(new Set(analystsForProcess.map((x) => String(x.id))))} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}>
                    Marcar todos os analistas do processo
                  </button>
                  <button type="button" onClick={() => setEquitableCurricularAnalysts(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}>
                    Limpar marcação
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkAssignCurricularEquitable}
                    disabled={selectedForBulkCurricular.size === 0 || equitableCurricularAnalysts.size < 2}
                    style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12, opacity: (selectedForBulkCurricular.size === 0 || equitableCurricularAnalysts.size < 2) ? 0.55 : 1 }}
                  >
                    Distribuir ({selectedForBulkCurricular.size} inscrições)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredApplications.length > 0 ? (
          <div style={{ ...styles.tableContainer, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ ...styles.table, tableLayout: 'auto', width: '100%', fontSize: '10px' }}>
                <colgroup>
                  {pendingCurricularForBulk.length > 0 && <col style={{ width: 30 }} />}
                  <col style={{ width: 52 }} />
                  <col />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: 140 }} />
                  {processUsesQuotas && <col style={{ width: 36 }} />}
                  <col style={{ width: 108 }} />
                  {isOrdemProcess && <col style={{ width: 148 }} />}
                  {!isOrdemProcess && <col style={{ width: 52 }} />}
                  <col style={{ width: 120 }} />
                  <col style={{ width: 150 }} />
                </colgroup>
                <thead>
                  <tr>
                    {pendingCurricularForBulk.length > 0 && (
                      <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thCheck }} title="Selecionar para atribuição em massa">☐</th>
                    )}
                    <th
                      role="columnheader"
                      aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                      style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thNum, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                      title="Ordenar por número da inscrição"
                    >
                      Nº{sortColumnIndicator(tableSort, 'id')}
                    </th>
                    <th
                      role="columnheader"
                      aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                      style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thText, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                      title="Ordenar por nome do candidato"
                    >
                      Candidato{sortColumnIndicator(tableSort, 'candidate')}
                    </th>
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thText }}>Curso</th>
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thUnit }}>Unidade</th>
                    {processUsesQuotas && (
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thQuota }}>Cota</th>
                    )}
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thStatus }}>Análise</th>
                    {isOrdemProcess && (
                    <th
                      role="columnheader"
                      aria-sort={tableSort.key === 'registered_at' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                      style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thText, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => setTableSort((p) => nextAdminTableSort(p, 'registered_at'))}
                      title="Ordenar por data e hora da inscrição (horário de Brasília)"
                    >
                      Inscrição{sortColumnIndicator(tableSort, 'registered_at')}
                    </th>
                    )}
                    {!isOrdemProcess && (
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thStatus }}>Nota</th>
                    )}
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thText }}>Analista curricular</th>
                    <th style={{ ...styles.tableHeaderCell, ...INSCRITOS_COL.thActions }}>Ações</th>
                  </tr>
                </thead>
              <tbody>
                {sortedApplications.map((application) => {
                  const process = processes.find(p => p.id === application.process_id);
                  const appProcessHasFee = !!process?.has_fee;
                  const paymentReady = isPaymentReadyForCurricular(application);
                    const podeAtribuirBulk = paymentReady &&
                    !application.analyst_id && !application.curricular_analyst_id &&
                    !application.excluded_at;
                  return (
                    <tr key={application.id}>
                      {pendingCurricularForBulk.length > 0 && (
                        <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdCheck }}>
                          {podeAtribuirBulk ? (
                            <input
                              type="checkbox"
                              checked={selectedForBulkCurricular.has(application.id)}
                              onChange={(e) => {
                                setSelectedForBulkCurricular(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(application.id);
                                  else next.delete(application.id);
                                  return next;
                                });
                              }}
                              style={{ margin: 0, width: '14px', height: '14px' }}
                            />
                          ) : null}
                        </td>
                      )}
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdNum }}>
                        #{application.id}
                      </td>
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdText }}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '10px' }}>
                          {application.candidate_name}
                        </div>
                        <div style={{ fontSize: '9px', color: '#6B7280' }}>
                          CPF: {application.candidate_cpf || 'N/A'}
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdText }}>
                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                          {application.course_name || 'N/A'}
                        </div>
                      </td>
                      <td
                        style={{ ...styles.tableCell, ...INSCRITOS_COL.tdUnit }}
                        title={application.course_campus || '—'}
                      >
                        {application.course_campus || '—'}
                      </td>
                      {processUsesQuotas && (
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdQuota }}>{application.quota_type || 'AC'}</td>
                      )}
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdStatus }}>
                        <span style={inscritosTableBadge({ ...getStatusBadgeStyle(getAnalysisStatusForDisplay(application)), ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px' })} title={getAnalysisStatusLabel(getAnalysisStatusForDisplay(application))}>
                          {getAnalysisStatusLabel(getAnalysisStatusForDisplay(application))}
                        </span>
                      </td>
                      {isOrdemProcess && (
                      <td
                        style={{ ...styles.tableCell, ...INSCRITOS_COL.tdText, fontSize: '9px', whiteSpace: 'nowrap' }}
                        title={application.created_at ? `Registro no sistema (UTC): ${application.created_at}` : ''}
                      >
                        {formatRegistrationDateTime(application.created_at)}
                      </td>
                      )}
                      {!isOrdemProcess && (
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdCompact }}>
                        {application.analysis_status === 'approved' && application.final_score != null && !isNaN(application.final_score)
                          ? <span style={{ fontWeight: '600', color: '#065F46', fontSize: '11px' }}>{Number(application.final_score).toFixed(2).replace('.', ',')}</span>
                          : <span style={{ color: '#9CA3AF', fontSize: '10px' }}>—</span>}
                      </td>
                      )}
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdStatus }}>
                        <span style={inscritosTableBadge({
                          ...getStatusBadgeStyle(
                            (application.analysis_status === 'returned' || (application.curricular_return_reason && !application.analyst_id && !application.curricular_analyst_id)) ? 'not_reassigned' :
                            (application.analyst_id || application.curricular_analyst_id) ? 'assigned' : 'not_assigned'
                          ),
                          ...styles.tableCellBadge,
                          fontSize: '9px',
                          padding: '4px 8px'
                        })} title={
                          application.analysis_status === 'returned' || (application.curricular_return_reason && !application.analyst_id && !application.curricular_analyst_id)
                            ? 'Devolvida'
                            : curricularAnalystCellLabel(application)
                        }>
                          {application.analysis_status === 'returned' || (application.curricular_return_reason && !application.analyst_id && !application.curricular_analyst_id) ? 'Devolvida' :
                           (application.analyst_id || application.curricular_analyst_id) ? curricularAnalystCellLabel(application) : 'Não atribuído'}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, ...INSCRITOS_COL.tdActions }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* Inscrição excluída: sem ações */}
                          {application.excluded_at ? (
                            <span style={{ fontSize: '9px', color: '#6B7280' }}>—</span>
                          ) : (
                          <>
                          {/* ATRIBUIR: Pago, Isento ou Recurso de Isenção Aprovado sem analista curricular (ou processo sem taxa) */}
                          {(() => {
                            const podeAtribuir = paymentReady &&
                              !application.analyst_id && !application.curricular_analyst_id &&
                              applicationRequiresLetterAnalysis(application);
                            if (podeAtribuir) {
                              return (
                                <button
                                  onClick={() => handleAssign(application, 'application')}
                                  style={{ ...getButtonStyle('assignment', 'small'), padding: '3px 5px', fontSize: '9px' }}
                                >
                                  {ACTION_ICONS.assign} Atribuir
                                </button>
                              );
                            }
                            return null;
                          })()}
                          {/* Outras ações (Reatribuir, Corrigir, etc) */}
                          {(() => {
                            // 1. Se já mostramos Atribuir acima, não duplicar (inclui exemption_rejected + paid)
                            if (paymentReady && !application.analyst_id && !application.curricular_analyst_id && applicationRequiresLetterAnalysis(application)) {
                              return null;
                            }
                            // 3a. Corrigir estado travado: returned + analyst_id (fluxo antigo)
                            const estadoTravado = application.analysis_status === 'returned' && 
                              (application.analyst_id || application.curricular_analyst_id);
                            if (estadoTravado) {
                              return (
                                <button
                                  onClick={async () => {
                                    const { error } = await apiClient.resetForReatribuicao(application.id);
                                    if (error) addNotification('Erro', error, 'error');
                                    else { addNotification('Sucesso', 'Corrigido. Botão Reatribuir disponível.', 'success'); loadData(); }
                                  }}
                                  style={{ ...getButtonStyle('secondary', 'small'), padding: '3px 5px', fontSize: '9px', backgroundColor: '#F59E0B', color: 'white', border: 'none' }}
                                  title="Corrigir estado travado (limpar analista para exibir Reatribuir)"
                                >
                                  {ACTION_ICONS.fix} Corrigir
                                </button>
                              );
                            }
                            // 3b. Reatribuir SOMENTE quando análise CURRICULAR foi devolvida (não considerar exemption_analyst_id)
                            const foiDevolvida = application.analysis_status === 'returned' || 
                              application.curricular_return_reason || application.curricular_returned_by;
                            const temAnalistaCurricular = application.analyst_id || application.curricular_analyst_id;
                            if (foiDevolvida && !temAnalistaCurricular) {
                              return (
                                <button
                                  onClick={() => handleAssign(application, 'reassignment')}
                                  style={{
                                    ...getButtonStyle('secondary', 'small'),
                                    padding: '3px 6px',
                                    fontSize: '9px',
                                    backgroundColor: '#8B5CF6',
                                    color: 'white',
                                    border: 'none'
                                  }}
                                  title={`Reatribuir para outro analista ${application.analyst_name ? `(atualmente: ${application.analyst_name})` : ''}`}
                                >
                                  {ACTION_ICONS.reassign} Reatribuir
                                </button>
                              );
                            }
                            
                            // 4. Se análise foi rejeitada - NENHUM BOTÃO (aguardando candidato enviar novos documentos)
                            if (application.analysis_status === 'rejected') {
                              return null;
                            }
                            
                            // 5. Nenhuma ação disponível
                            return null;
                          })()}
                          {/* Visualizar quando situação for Deferida ou Indeferida */}
                          {(application.analysis_status === 'approved' || application.analysis_status === 'rejected') && (
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          )}
                          {/* Botão Analisar: sem analista OU admin editando sua própria análise */}
                          {(() => {
                            const semAnalista = !application.analyst_id && !application.curricular_analyst_id;
                            const podeAnalisar = semAnalista && paymentReady;
                            const adminEditandoSua = user?.id && (Number(application.analyst_id) === Number(user.id)) &&
                              (application.analysis_status === 'approved' || application.analysis_status === 'rejected');
                            if (!podeAnalisar && !adminEditandoSua) return null;
                            const label = adminEditandoSua ? 'Editar' : 'Analisar';
                            return (
                              <button
                                onClick={() => handleAnalyze(application)}
                                style={getButtonStyle('primary', 'small')}
                                title={adminEditandoSua ? 'Editar análise' : 'Analisar'}
                              >
                                {adminEditandoSua ? '✏️' : ACTION_ICONS.analyze} {label}
                              </button>
                            );
                          })()}
                          {/* Mensagem de aguardando pagamento - só quando processo cobra taxa e pagamento pendente */}
                          {appProcessHasFee && getPaymentStatusForAnalysis(application) === 'pending' && (
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              Aguardando pagamento
                            </span>
                          )}
                          {/* Excluir inscrição (admin - para testes) */}
                          <button
                            onClick={() => handleDeleteApplication(application)}
                            title="Excluir inscrição e todos os dados relacionados"
                            style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', backgroundColor: '#FEE2E2', color: '#991B1B', cursor: 'pointer', fontSize: '14px' }}
                          >
                            {ACTION_ICONS.delete}
                          </button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>Nenhuma inscrição encontrada</p>
          </div>
        )}
      </div>

      {/* Modal de Análise */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title={isOrdemProcess ? 'Análise de Inscrição' : 'Análise Curricular'}
        large
      >
        {selectedApplicationForAnalysis && (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>
                Informações da Inscrição
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <strong>Candidato:</strong> {selectedApplicationForAnalysis.candidate_name}
                </div>
                <div>
                  <strong>CPF:</strong> {selectedApplicationForAnalysis.candidate_cpf || 'Não informado'}
                </div>
                <div>
                  <strong>Curso:</strong> {selectedApplicationForAnalysis.course_name}<br/>
                  <strong>Unidade:</strong> {selectedApplicationForAnalysis.course_campus || '—'}
                </div>
                <div>
                  <strong>Processo:</strong> {processes.find(p => p.id === selectedApplicationForAnalysis.process_id)?.name || `Processo ${selectedApplicationForAnalysis.process_id}`}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>
                Documentos Anexados
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(selectedApplicationForAnalysis.curricular_documents || selectedApplicationForAnalysis.documents || [])?.map((doc, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <span>{doc.name}</span>
                    <button
                      onClick={() => handleDownloadDocuments(selectedApplicationForAnalysis, index)}
                      style={{
                        ...styles.button,
                        ...styles.buttonSecondary,
                        ...styles.buttonSmall
                      }}
                    >
                      📄 Download
                    </button>
                  </div>
                )) || (
                  <p style={{ color: '#6B7280', fontStyle: 'italic' }}>
                    Nenhum documento anexado
                  </p>
                )}
              </div>
            </div>

            {!isOrdemProcess && (
            <>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>
                📊 Lançamento de Notas
              </h4>
              {(() => {
                const course = courses.find(c => c.id === selectedApplicationForAnalysis.course_id);
                return course?.subjects?.map((subject, serieIndex) => (
                  <div key={serieIndex} style={{
                    backgroundColor: '#FEF7F0',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid #FED7AA'
                  }}>
                    <h5 style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#EA580C', 
                      marginBottom: '12px' 
                    }}>
                      📖 {subject.serie}
                    </h5>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {subject.disciplinas.map((disciplina, discIndex) => (
                        <div key={discIndex} style={{
                          backgroundColor: 'white',
                          padding: '12px',
                          borderRadius: '6px',
                          border: '1px solid #FED7AA'
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#9A3412',
                            marginBottom: '4px'
                          }}>
                            {disciplina.name} (Peso: {disciplina.weight})
                          </label>
                          <GradeInput
                            value={analysisForm.grades[subject.serie]?.[disciplina.name]}
                            onChange={(v) => handleGradeChange(subject.serie, disciplina.name, v)}
                            style={styles.gradeInput}
                            placeholder="0,00"
                            max={100}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )) || (
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#FEF3C7', 
                    borderRadius: '8px',
                    border: '1px solid #F59E0B'
                  }}>
                    <p style={{ margin: '0', color: '#92400E', fontSize: '14px' }}>
                      Disciplinas não configuradas para este curso
                    </p>
                  </div>
                );
              })()}
            </div>

            <div style={{
              backgroundColor: '#F0FDF4',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #BBF7D0',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', margin: 0 }}>
                  🧮 Média Ponderada Final
                </h4>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#166534' }}>
                  {calculateCurrentAverage().toFixed(2)}
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#15803D', marginTop: '8px', margin: 0 }}>
                💡 Fórmula: (Média Ponderada Série 1 + Média Ponderada Série 2 + ...) ÷ Número de Séries
              </p>
            </div>
            </>
            )}

            {isOrdemProcess && (
              <div style={{
                backgroundColor: '#ECFDF5',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #BBF7D0',
                marginBottom: '24px'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#047857', lineHeight: 1.5 }}>
                  <strong>Por ordem de inscrição:</strong> verifique os documentos e registre deferimento ou indeferimento.
                  A classificação considera a ordem de inscrição entre os candidatos deferidos.
                </p>
              </div>
            )}

            {/* Situação e Observações */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={styles.label}>📊 Situação da Análise</label>
                  <select
                    value={analysisForm.status}
                    onChange={(e) => setAnalysisForm({
                      ...analysisForm, 
                      status: e.target.value
                    })}
                    style={styles.select}
                  >
                    <option value="pending">Em Análise</option>
                    <option value="approved">Deferida</option>
                    <option value="rejected">Indeferida</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>
                    📝 Motivo de Indeferimento/Devolução <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    value={analysisForm.rejection_reason}
                    onChange={(e) => setAnalysisForm({
                      ...analysisForm, 
                      rejection_reason: e.target.value
                    })}
                    style={styles.select}
                    disabled={analysisForm.status !== 'rejected'}
                  >
                    <option value="">Selecione um motivo...</option>
                    {rejectionReasons.map(reason => (
                      <option key={reason.id} value={reason.id}>
                        {reason.title}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    Obrigatório quando a situação for "Indeferida"
                  </div>
                </div>
              </div>
              
              <div>
                <label style={styles.label}>📝 Observações da Análise</label>
                <textarea
                  value={analysisForm.notes}
                  onChange={(e) => setAnalysisForm({...analysisForm, notes: e.target.value})}
                  style={styles.textarea}
                  placeholder="Observações sobre a análise da inscrição..."
                  rows="3"
                />
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => setShowAnalysisModal(false)}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary
                }}
              >
                ❌ Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (validateRejectionReason()) {
                    saveAnalysis();
                  }
                }}
                style={{
                  ...styles.button,
                  ...styles.buttonPrimary
                }}
              >
                💾 Salvar Análise
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Atribuição/Reatribuição */}
      <Modal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        title={assignmentType === 'reassignment' ? 'Reatribuir Análise' : 'Atribuir Análise'}
      >
        <div style={{ padding: '20px' }}>
          {/* Informações sobre reatribuição */}
          {assignmentType === 'reassignment' && selectedItem && (
            <div style={{ 
              backgroundColor: '#FEF3C7', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #F59E0B'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#92400E' }}>
                🔄 Reatribuição de Análise
              </div>
              <div style={{ fontSize: '14px', color: '#92400E' }}>
                <strong>Candidato:</strong> {selectedItem.candidate_name}<br/>
                <strong>Analista Atual:</strong> {selectedItem.analyst_name}<br/>
                <strong>Status Atual:</strong> {getAnalysisStatusLabel(selectedItem.analysis_status)}
              </div>
            </div>
          )}
          {/* Motivo da devolução (análise curricular) - igual ao fluxo de isenção */}
          {(selectedItem?.curricular_return_reason || selectedItem?.curricular_returned_by_name) && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#FEF3C7',
              borderRadius: '6px',
              border: '1px solid #F59E0B',
              fontSize: '13px',
              color: '#92400E'
            }}>
              <strong>↩️ Última devolução (análise curricular):</strong>
              {selectedItem.curricular_returned_by_name && (
                <p style={{ margin: '4px 0 0 0' }}>
                  <strong>Devolvido por:</strong> {selectedItem.curricular_returned_by_name}
                  {selectedItem.curricular_returned_at && (
                    <span> em {new Date(selectedItem.curricular_returned_at).toLocaleString('pt-BR')}</span>
                  )}
                </p>
              )}
              {selectedItem.curricular_return_reason && (
                <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
                  <strong>Motivo:</strong> {selectedItem.curricular_return_reason}
                </p>
              )}
            </div>
          )}
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              {assignmentType === 'reassignment' ? 'Selecionar Novo Analista:' : 'Selecionar Analista:'}
            </label>
            <select
              value={selectedAnalyst}
              onChange={(e) => setSelectedAnalyst(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">Selecione um analista</option>
              {analystsForProcess.map(analyst => (
                <option key={analyst.id} value={analyst.id}>
                  {analyst.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowAssignmentModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedAnalyst) {
                  handleAssignmentSubmit();
                } else {
                  setError('Selecione um analista');
                }
              }}
              style={{
                ...styles.button,
                ...styles.buttonPrimary
              }}
            >
              {assignmentType === 'reassignment' ? `${ACTION_ICONS.reassign} Reatribuir` : `${ACTION_ICONS.assign} Atribuir`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Ver Inscrição - acesso completo à análise curricular (somente leitura) */}
      {showViewModal && selectedApplicationToView && (() => {
        const analysisRec = analyses.find(a => a.application_id === selectedApplicationToView.id);
        const finalScore = analysisRec?.final_score ?? selectedApplicationToView.final_score ?? null;
        const curricularDocs = selectedApplicationToView.curricular_documents || selectedApplicationToView.documents || [];
        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            minWidth: '450px',
            maxWidth: '600px',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
              {ACTION_ICONS.view} Detalhes da Inscrição e Análise Curricular
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #E5E7EB' }}>
              <button
                onClick={() => setViewModalTab('details')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewModalTab === 'details' ? '#3B82F6' : 'transparent',
                  color: viewModalTab === 'details' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Detalhes
              </button>
              <button
                onClick={() => setViewModalTab('history')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewModalTab === 'history' ? '#3B82F6' : 'transparent',
                  color: viewModalTab === 'history' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Histórico
              </button>
            </div>
            {viewModalTab === 'details' && (
            <>
            <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6, marginBottom: '20px' }}>
              <p><strong>Candidato:</strong> {selectedApplicationToView.candidate_name || 'N/A'}</p>
              <p><strong>CPF:</strong> {selectedApplicationToView.candidate_cpf || 'N/A'}</p>
              <p><strong>Processo:</strong> {processes.find(p => p.id === selectedApplicationToView.process_id)?.name || 'N/A'}</p>
              <p><strong>Curso:</strong> {selectedApplicationToView.course_name || 'N/A'}</p>
              <p><strong>Unidade:</strong> {selectedApplicationToView.course_campus || '—'}</p>
              <p><strong>Analista curricular:</strong> {curricularAnalystCellLabel(selectedApplicationToView)}</p>
              <p><strong>Situação:</strong> {getAnalysisStatusLabel(selectedApplicationToView.analysis_status)}</p>
              {finalScore != null && (
                <p><strong>Nota Final:</strong> <span style={{ fontWeight: '600', color: '#166534' }}>{parseFloat(finalScore).toFixed(2)}</span></p>
              )}
            </div>

            {selectedApplicationToView.curricular_return_reason && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#991B1B' }}>
                  <strong>Motivo da devolução:</strong> {selectedApplicationToView.curricular_return_reason}
                </p>
              </div>
            )}

            {selectedApplicationToView.analysis_status === 'rejected' && (selectedApplicationToView.curricular_rejection_reason || selectedApplicationToView.rejection_reason) && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#991B1B' }}>
                  <strong>Motivo do indeferimento:</strong> {(() => {
                    const r = selectedApplicationToView.curricular_rejection_reason || selectedApplicationToView.rejection_reason;
                    const reason = rejectionReasons.find(x => x.id == r);
                    return reason ? reason.title : r || 'Não especificado';
                  })()}
                </p>
              </div>
            )}

            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#166534' }}>📄 Documentos Curriculares</h4>
              {curricularDocs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {curricularDocs.map((doc, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #BBF7D0' }}>
                      <span style={{ fontSize: '13px' }}>📄 {doc.name || `Documento ${idx + 1}`}</span>
                      <button
                        onClick={() => handleDownloadDocuments(selectedApplicationToView, idx)}
                        style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 10px', fontSize: '12px' }}
                      >
                        Baixar
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum documento anexado</p>
              )}
            </div>
            </>
            )}
            {viewModalTab === 'history' && (
              <div style={{ marginBottom: '20px' }}>
                {historyLoading ? (
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>Carregando histórico...</p>
                ) : analysisHistory.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum registro de histórico.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Analista</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Tipo</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Ação</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>{historyScoreColumnLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisHistory.map((r, i) => (
                          <tr key={r.id || i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                            <td style={{ padding: '8px' }}>{r.created_at ? formatDate(r.created_at) : '—'}</td>
                            <td style={{ padding: '8px' }}>{r.analyst_name || '—'}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryTypeLabel(r.analysis_type, r.analysis_type_label)}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryActionLabel(r.action, r.action_label)}</td>
                            <td style={{ padding: '8px' }}>{r.final_score != null ? parseFloat(r.final_score).toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowViewModal(false); setSelectedApplicationToView(null); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default AnalysisManager; 